/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback } from "react";
import { Button, FormControlLabel, Checkbox, Tooltip, Chip, Card } from "@material-ui/core";
import ChipInput from "../../../components/ChipInput/ChipInput";
import { SCHEDULED_REPORT_TOOLTIPS } from "../../../utility/toolTipMessages";
import PropTypes from "prop-types";
import { useScheduledReports } from "../context/scheduledReports";
import { isValidEmail } from "../../../utility";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { CONNECT_REPORT_ENDING_PATTERN } from "../../../utility/constants";

const customChipRenderer = ({ _value, text, isFocused, handleClick, handleDelete, className }, key) => {
	const isValid = isValidEmail(text);
	return (
		<Chip
			key={key}
			className={className}
			variant="outlined"
			style={{
				backgroundColor: isFocused ? "rgba(0, 0, 0, 0.04)" : undefined,
				color: !isValid ? "red" : undefined,
				fontWeight: !isValid ? "bolder" : undefined,
			}}
			title={!isValid ? "This email address is invalid." : ""}
			onClick={handleClick}
			onDelete={handleDelete}
			disabled={false}
			label={text}
		/>
	);
};

const validateEmailList = (emailAddressList) => {
	if (emailAddressList.length === 0) {
		return "At least one email address is required.";
	} else if (emailAddressList.find((email) => !isValidEmail(email))) {
		return "At least one email address is invalid.";
	} else {
		return false;
	}
};

const ScheduledReportCard = ({ scheduledReport, scheduledReports, formEdited, setFormEdited, reportIndex }) => {
	const { removeScheduledReports, updateSingleScheduledReports } = useScheduledReports();
	const EMAIL_LIMIT = 5;
	const [emailError, setEmailError] = React.useState(validateEmailList(scheduledReport.emailAddressList));
	const getPathError = useCallback(
		(newPath) => {
			if (!newPath) {
				return "Report file name is required.";
			}
			if (scheduledReports.find((x, index) => index !== reportIndex && x.reportPath === newPath)) {
				return "Report file name already exists.";
			}
			if (!newPath.endsWith(CONNECT_REPORT_ENDING_PATTERN)) {
				return "File name is not a valid connect report file name.";
			}
		},
		[scheduledReports, reportIndex],
	);

	const updateEmailAddress = (emailAddressList) => {
		const newEmailError = validateEmailList(emailAddressList);
		if (newEmailError) {
			setEmailError(newEmailError);
		} else {
			setEmailError("");
		}
		updateSingleScheduledReports({ ...scheduledReport, emailAddressList: emailAddressList }, reportIndex);
	};
	const updateReportPath = (_valueName, value) => {
		let newScheduledReport = { ...scheduledReport };
		const nameIndex = value.lastIndexOf(CONNECT_REPORT_ENDING_PATTERN);

		let name =
			nameIndex > -1 ? (
				value.substring(value.lastIndexOf("/") + 1, nameIndex)
			) : (
				<span style={{ color: "red" }}>Invalid Report Name</span>
			);
		newScheduledReport.reportName = name;
		newScheduledReport.reportPath = value;
		newScheduledReport.reportId =
			value.substring(0, value.lastIndexOf(CONNECT_REPORT_ENDING_PATTERN)) || "PACE_NEW_REPORT";
		updateSingleScheduledReports(newScheduledReport, reportIndex);
	};
	const handleActiveChange = (event) => {
		const newScheduledReport = { ...scheduledReport };
		newScheduledReport.active = !event.target.checked;
		updateSingleScheduledReports({ ...newScheduledReport }, reportIndex);
	};
	return (
		<Card style={{ marginBottom: "20px", padding: "10px" }} variant="outlined">
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<h6>{scheduledReport.reportName || "New Report"}</h6>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "flex-end",
					}}
				>
					<FormControlLabel
						value="top"
						style={{ display: "flex", alignItems: "center", margin: "-4px 5px 0 0" }}
						control={
							<Checkbox
								checked={!scheduledReport.active}
								color="secondary"
								size="small"
								onChange={(event) => {
									handleActiveChange(event);
									setFormEdited(true);
								}}
							/>
						}
						label={
							<span style={{ fontSize: 14, marginLeft: -6 }}>
								<span>Disable</span>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title="If a check is disable it will not show up in the page."
								>
									<i style={{ height: "15px", marginLeft: "4px", color: "gray" }} className="fas fa-info-circle" />
								</Tooltip>
							</span>
						}
					/>
					<Button
						size="small"
						variant="outlined"
						style={{ color: "#cc1f20" }}
						onClick={() => {
							removeScheduledReports(scheduledReport.reportId, reportIndex);
							setFormEdited(true);
						}}
					>
						Remove
					</Button>
				</div>
			</div>
			<div className="d-flex mb-4 position-relative">
				<FormTextField
					label={"Report file name"}
					formEdited={formEdited}
					setEdited={setFormEdited}
					startingValue={scheduledReport.reportPath}
					setConfigValue={updateReportPath}
					valueName={"reportPath"}
					styleObject={{ minWidth: 120, flex: 1 }}
					enableDebounce={false}
					errorCheck={(newPath) => getPathError(newPath)}
					toolTipMessage={SCHEDULED_REPORT_TOOLTIPS.REPORT_FILE_NAME}
				/>
			</div>
			<div style={{ marginTop: "-20px" }} className="d-flex mb-4 position-relative">
				<div style={{ position: "absolute", bottom: "10px", zIndex: 1 }}>
					<Tooltip arrow interactive placement={"bottom-start"} title={SCHEDULED_REPORT_TOOLTIPS.REPORT_EMAIL_ADDRESS}>
						<i style={{ height: "15px", color: "gray", bottom: "5px" }} className="fas fa-info-circle" />
					</Tooltip>
				</div>
				<ChipInput
					blurBehavior="add-or-clear"
					style={{ marginTop: "15px", minWidth: 120, flex: 1, paddingLeft: "25px" }}
					error={!!emailError}
					helperText={emailError}
					label="Email address"
					disabled={scheduledReport.emailAddressList.length === EMAIL_LIMIT}
					fullWidth
					chipRenderer={customChipRenderer}
					value={scheduledReport.emailAddressList}
					onAdd={(chip) => {
						if (scheduledReport.emailAddressList.length < EMAIL_LIMIT) {
							updateEmailAddress([...scheduledReport.emailAddressList, chip]);
							setFormEdited(true);
						}
					}}
					onDelete={(chip) => {
						const email = scheduledReport.emailAddressList.filter((t) => t !== chip);
						updateEmailAddress(email);
						setFormEdited(true);
					}}
					InputLabelProps={{
						shrink: true,
					}}
				/>
			</div>
		</Card>
	);
};

ScheduledReportCard.defaultProps = {
	scheduledReport: {},
};
ScheduledReportCard.propTypes = {
	scheduledReport: PropTypes.object,
};

export default ScheduledReportCard;
